import {
  faExclamationCircle,
  faUpload as faUploadRegular,
} from '@fortawesome/pro-regular-svg-icons';
import { faFile, faUpload, faUser, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  CheckIcon,
  Flex,
  Heading,
  RadioButton,
  Text,
  TextButton,
  Tooltip,
} from '@modusign/design-system';
import { getLabelList } from '@modusign/openapi-client-category';
import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FormProvider, useForm } from 'react-hook-form-v7';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { Prompt, useHistory } from 'react-router';
import { v4 } from 'uuid';

import FileUploadErrorModal from '@app/components/FileUploadErrorModal';
import {
  FileUploadErrorBuilder,
  IFileUploadError,
} from '@app/components/FileUploadErrorModal/fileUploadErrorBuilder';
import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import LabelSection from '@app/features/externalDocument/components/UploadPage/LabelSection';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import usePreventUnload from '@app/hooks/usePreventUnload';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { validateAttachmentFile, validateDocumentFile } from '@app/libs/file/validateFile';

import { MAXIMUM_FILE_LIMIT } from '../../../constants';
import useOrdering from '../../../hooks/useOrdering';
import checkIsContractableFileName from '../../../libs/checkIsContractableFileName';
import { startUpload } from '../../../state';
import BasicInformationEditor, { BasicInformationFormState } from '../../BasicInformationEditor';
import ParticipantsEditor, { ParticipantFormState } from '../../ParticipantsEditor';
import Accordion from '../Accordion';
import deduplicateFileNameFactory from '../deduplicateFileNameFactory';
import NotAnOriginalDocumentInlineAlert from '../NotAnOriginalDocumentInlineAlert';

type NamedFile = {
  name: string;
  file: File;
  isContractFile: boolean;
};

type FormState = BasicInformationFormState & ParticipantFormState;

export default function SingleUploadPanel() {
  const workspaceId = useWorkspaceId();

  const history = useHistory();
  const dispatch = useDispatch();

  const { createErrorHandler } = useOpenErrorModal();

  const [participantIds, setParticipantIds] = useState<string[]>([]);
  const [isBasicInformationAccordionOpen, setIsBasicInformationAccordionOpen] = useState(false);
  const [isSignerAccordionOpen, setIsSignerAccordionOpen] = useState(false);

  const { initOrdering } = useOrdering();

  const [labelIds, setLabelIds] = useState<string[]>([]);

  const formMethods = useForm<FormState>({
    mode: 'onChange',
  });

  const {
    formState: { isValid, isDirty },
    getValues,
  } = formMethods;

  const [files, setFiles] = useState<Array<{ name: string; isContractFile: boolean; file: File }>>(
    []
  );

  const preventUnload = useRef(isDirty || !!files.length);
  preventUnload.current = isDirty || !!files.length;
  usePreventUnload(preventUnload.current);

  let tooltip: string | null = null;
  if (files.length === 0) {
    tooltip = '파일을 업로드해 주세요.';
  } else if (!files.some((f) => f.isContractFile)) {
    tooltip = 'PDF, HWP, DOCS등 계약서로 설정할 수 있는 파일을 업로드해 주세요.';
  } else if (!isValid) {
    tooltip = '계약 제목, 담당자 정보, 서명자 정보를 입력해 주세요.';
  }

  const getLabelsEnsureLabelId = async () => {
    if (labelIds.length === 0) {
      return [];
    }
    const { labels } = await getLabelList(workspaceId, { labelIds });
    return labels;
  };

  const { mutate, isLoading } = useMutation(
    async (data: FormState) => {
      const labels = await getLabelsEnsureLabelId();
      dispatch(
        startUpload({
          documents: [
            {
              name: data.title,
              files: files.map((f) => ({
                file: f.file,
                name: f.name,
                isContract: f.isContractFile,
              })),
              metadata: {
                personInCharge: {
                  name: data.personInCharge.name,
                  email: data.personInCharge.contact?.email || null,
                  phone: data.personInCharge.contact?.phone || null,
                },
                signers: participantIds.map((id) => {
                  const participant = data.participants[id];
                  const personInCharge =
                    participant.personInCharge?.name ||
                    participant.personInCharge?.email ||
                    participant.personInCharge?.phone
                      ? {
                          name: participant.personInCharge?.name || null,
                          email: participant.personInCharge?.email || null,
                          phone: participant.personInCharge?.phone || null,
                        }
                      : null;
                  return {
                    externalSigner: participant.externalSigner,
                    name: participant.name,
                    email: participant.contact?.email || null,
                    phone: participant.contact?.phone || null,
                    type: participant.type || 'INDIVIDUAL',
                    company: participant.company || null,
                    personInCharge,
                  };
                }),
                type: data.type || undefined,
                dateOfCompletion: data.dateOfCompletion || undefined,
                documentPeriod: data.documentPeriod.from
                  ? { from: data.documentPeriod.from, to: data.documentPeriod.to }
                  : undefined,
                memo: data.memo || undefined,
                originalLocation: data.originalLocation || undefined,
                renewal: data.renewal
                  ? {
                      period: { unit: 'year', value: data.renewal.periodInYears },
                      noticeBeforeExpiration:
                        data.renewal.noticeBeforeExpirationInDays === -1
                          ? null
                          : {
                              unit: 'day',
                              value: data.renewal.noticeBeforeExpirationInDays,
                            },
                    }
                  : undefined,
                status: data.status || undefined,
                labels,
              },
            },
          ],
        })
      );
      initOrdering();
      preventUnload.current = false;
      history.push(`/external-documents/list`);
    },
    {
      onError: createErrorHandler({ title: '계약 등록 실패' }),
    }
  );

  const handleSubmit = formMethods.handleSubmit((data) => mutate(data));

  return (
    <>
      <Prompt
        message={() => {
          if (preventUnload.current) {
            return '지금 이 페이지를 벗어날 경우, 진행 중인 작업이 중단됩니다.';
          }
          return true;
        }}
      />
      {isLoading && <GlobalLoadingSpinner />}
      <Heading as="h2" textAlign="center">
        한 개씩 등록
      </Heading>
      <Text
        as="div"
        color="textNeutralLight"
        sx={{
          borderBottom: '1px solid',
          borderBottomColor: 'dividerNeutralLight',
          pb: 8,
          mt: 3,
        }}
        textAlign="center"
      >
        파일을 업로드하고 계약 정보를 입력하여 계약을 등록할 수 있습니다.
        <br />
        미리 정보를 입력해두면 더 편하게 관리할 수 있습니다.
      </Text>
      <FileUploadDropzone
        namedFiles={files}
        setNamedFiles={(files) => {
          setFiles(files);
          if (files.length) {
            setIsBasicInformationAccordionOpen(true);
          }
        }}
      />
      <FormProvider {...formMethods}>
        <Accordion
          index={2}
          isOpen={isBasicInformationAccordionOpen}
          required
          summary={() => (
            <Flex flexDirection="column">
              <Flex alignItems="center">
                <Flex
                  alignItems="center"
                  color="iconNeutralBase"
                  justifyContent="center"
                  width="22px"
                >
                  <FontAwesomeIcon icon={faFile} style={{ fontSize: '12px' }} />
                </Flex>
                <Text color="textNeutralLight">계약 제목</Text>
                <Text ml={2}>{getValues('title') || '-'}</Text>
              </Flex>
              <Flex alignItems="center">
                <Flex
                  alignItems="center"
                  color="iconNeutralBase"
                  justifyContent="center"
                  width="22px"
                >
                  <FontAwesomeIcon icon={faUser} style={{ fontSize: '12px' }} />
                </Flex>
                <Text color="textNeutralLight">담당자</Text>
                <Text ml={2}>{(getValues('personInCharge.name') as string) || '-'}</Text>
              </Flex>
            </Flex>
          )}
          title="계약 정보"
          onToggle={setIsBasicInformationAccordionOpen}
        >
          <BasicInformationEditor focusTitleInput={isBasicInformationAccordionOpen} />
        </Accordion>
        <Accordion
          index={3}
          isOpen={isSignerAccordionOpen}
          summary={
            <Flex alignItems="center">
              <Flex
                alignItems="center"
                color="iconNeutralBase"
                justifyContent="center"
                width="22px"
              >
                <FontAwesomeIcon icon={faUser} style={{ fontSize: '12px' }} />
              </Flex>
              <Text ml={2}>{participantIds.length}명</Text>
            </Flex>
          }
          title="서명자"
          onToggle={(isOpen) => {
            setIsSignerAccordionOpen(isOpen);
            if (isOpen && participantIds.length === 0) {
              setParticipantIds([v4()]);
            }
          }}
        >
          <ParticipantsEditor
            getPersonInCharge={() => {
              const name = getValues('personInCharge.name') as string;
              const contact = getValues('personInCharge.contact') as
                | {
                    phone: string;
                    email: string;
                  }
                | undefined;

              if (name || contact?.email || contact?.phone) {
                return {
                  name,
                  email: contact?.email || null,
                  phone: contact?.phone || null,
                };
              }

              return null;
            }}
            participantIds={participantIds}
            setParticipantIds={setParticipantIds}
          />
        </Accordion>
        <LabelSection index={4} labelIds={labelIds} onLabelChange={setLabelIds} />
        <Box textAlign="center">
          <Tooltip content={tooltip} isOpen={tooltip ? undefined : false} placement="top">
            <Button
              disabled={!!tooltip}
              height="42px"
              mt="66px"
              variant="primary"
              width="180px"
              onClick={handleSubmit}
            >
              등록하기
            </Button>
          </Tooltip>
        </Box>
        <NotAnOriginalDocumentInlineAlert sx={{ mt: '54px' }} />
      </FormProvider>
    </>
  );
}

interface FileUploadDropzoneProps {
  namedFiles: Array<NamedFile>;
  setNamedFiles: Dispatch<SetStateAction<NamedFile[]>>;
}

function FileUploadDropzone({ namedFiles, setNamedFiles }: FileUploadDropzoneProps) {
  const hasContractFile = namedFiles.some((f) => f.isContractFile);
  const [contractFileTooltipIndex, setContractFileTooltipIndex] = useState<number | null>(null);
  const [errors, setErrors] = useState<IFileUploadError[] | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    noClick: true,
    useFsAccessApi: false,
    onDropAccepted: async (newFiles) => {
      try {
        setIsValidating(true);
        const acceptedFiles: File[] = [];
        const rejectedFileErrors: IFileUploadError[] = [];

        await Promise.all(
          newFiles.map(async (file) => {
            if (file.name === '.DS_Store') {
              return;
            }
            const errorBuilder = new FileUploadErrorBuilder(file.name);

            if (acceptedFiles.length + namedFiles.length + 1 > MAXIMUM_FILE_LIMIT) {
              errorBuilder.fileNumberExceededError(MAXIMUM_FILE_LIMIT);
            }

            // eslint-disable-next-line no-await-in-loop
            const validationResults: [boolean, boolean] = await Promise.all([
              validateDocumentFile(file),
              validateAttachmentFile(file),
            ]);

            if (validationResults.every((r) => !r)) {
              errorBuilder.fileExtensionError();
            }

            if (file.size > 10 * 1000 * 1000) {
              errorBuilder.fileSizeExceededError(10 * 1000 * 1000);
            }

            const e = errorBuilder.build();

            if (e) {
              rejectedFileErrors.push(e);
              return;
            }
            acceptedFiles.push(file);
          })
        );

        if (rejectedFileErrors.length > 0) {
          setErrors(rejectedFileErrors);
        }

        setNamedFiles((files) => {
          const deduplicateFileName = deduplicateFileNameFactory(files.map((f) => f.name));

          const result = files.concat(
            acceptedFiles.map((file) => ({
              name: deduplicateFileName(file.name),
              isContractFile: false,
              file,
            }))
          );

          // 처음 선택된 계약서로 설정 가능한 파일을 계약서로 설정하고 안내 툴팁을 3초간 띄웁니다.
          if (result.every((f) => !f.isContractFile)) {
            const index = result.findIndex((f) => checkIsContractableFileName(f.name));
            if (index >= 0 && !result[index].isContractFile) {
              result[index].isContractFile = true;
              setContractFileTooltipIndex(index);
              setTimeout(() => setContractFileTooltipIndex(null), 3000);
            }
          }

          return result;
        });
      } finally {
        setIsValidating(false);
      }
    },
  });

  return (
    <Box as="section" borderBottom="1px solid" borderBottomColor="dividerNeutralLight" pb={7}>
      <FileUploadErrorModal errors={errors} onConfirm={() => setErrors(null)} />
      <Flex alignItems="center" justifyContent="space-between" my={8} width="100%">
        <Flex alignItems="center" width="200px">
          <Flex
            alignItems="center"
            bg="statusBackgroundPrimaryBase"
            borderRadius="50%"
            fontSize="10px"
            fontWeight="bold"
            height="21px"
            justifyContent="center"
            width="21px"
          >
            1
          </Flex>
          <Heading as="h3" flex="auto" fontSize="md" ml={3}>
            파일 업로드
          </Heading>
        </Flex>
        <TextButton color="textNeutralLight" onClick={open}>
          <FontAwesomeIcon icon={faUploadRegular} /> 업로드
        </TextButton>
      </Flex>
      {namedFiles.length > 0 && !hasContractFile && (
        <Flex
          alignItems="center"
          bg="notificationBackgroundPrimaryBase"
          border="1px solid"
          borderColor="boxBorderPrimaryBase"
          borderRadius="3px"
          color="textNeutralSlightlyLight"
          fontSize="sm"
          mb={4}
          px={5}
          py={4}
        >
          <Text color="darkGray200" fontSize="12px" mr={2}>
            <FontAwesomeIcon icon={faExclamationCircle} />
          </Text>
          계약을 등록하려면 PDF, HWP, DOCS등 계약서로 설정할 수 있는 파일이 있어야합니다.
        </Flex>
      )}

      <Box {...getRootProps()} height="240px">
        <input {...getInputProps()} />
        {isDragActive && (
          <Flex
            alignItems="center"
            bg="boxBackgroundEmphasisBase"
            border="1px dashed"
            borderColor="boxBorderEmphasisBase"
            color="textEmphasisBase"
            fontWeight="medium"
            height="100%"
            justifyContent="center"
          >
            여기에 파일을 놓아 주세요.
          </Flex>
        )}
        {!isDragActive &&
          (namedFiles.length > 0 ? (
            <Flex
              alignItems="strech"
              border="1px solid"
              borderColor="#e0e0e0"
              flexDirection="column"
              height="100%"
              width="100%"
            >
              <Text
                as="div"
                bg="tableBackgroundNeutralSlightlyDark"
                color="textNeutralVeryLight"
                sx={{
                  borderBottom: '1px solid',
                  borderBottomColor: 'tableBorderNeutralBase',
                  px: 4,
                  py: 3,
                }}
              >
                파일
              </Text>
              <Box as="ul" overflow="auto">
                {namedFiles.map((file, index) => {
                  const showContractFileTooltip = contractFileTooltipIndex === index;
                  const canBeContractFile = checkIsContractableFileName(file.name);

                  let tooltip = '';
                  let isTooltipOpen: boolean | undefined = false;

                  if (showContractFileTooltip) {
                    tooltip = '계약서로 설정할 파일을 선택해 주세요.';
                    isTooltipOpen = true;
                  } else if (!canBeContractFile) {
                    tooltip = '이 확장자 파일은 계약서로 설정할 수 없습니다.';
                    isTooltipOpen = undefined;
                  }

                  return (
                    <Flex
                      alignItems="center"
                      as="li"
                      borderBottom="1px solid"
                      borderBottomColor="tableBorderNeutralLight"
                      key={file.name}
                      pl={5}
                      pr={4}
                      py={2}
                    >
                      <Tooltip content={tooltip} isOpen={isTooltipOpen} placement="left">
                        <RadioButton
                          aria-label="계약서로 설정하기"
                          checked={file.isContractFile}
                          disabled={!canBeContractFile}
                          sx={{ flex: 'none' }}
                          onClick={() =>
                            setNamedFiles((files) =>
                              files.map((file, i) => ({ ...file, isContractFile: i === index }))
                            )
                          }
                        />
                      </Tooltip>
                      <Flex alignItems="center" flex="auto">
                        <Text
                          ml={5}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {file.name}
                        </Text>
                        {file.isContractFile && (
                          <Text
                            bg="statusBackgroundPositiveUltraLight"
                            color="textPositiveBase"
                            fontSize="xm"
                            ml={3}
                            sx={{
                              borderRadius: '3px',
                              flex: 'none',
                              py: '2px',
                              px: 2,
                            }}
                          >
                            계약서
                          </Text>
                        )}
                      </Flex>
                      <Flex
                        alignItems="center"
                        aria-label="파일 제거하기"
                        as="button"
                        flex="none"
                        height="30px"
                        justifyContent="center"
                        type="button"
                        width="36px"
                        onClick={() =>
                          setNamedFiles((files) => {
                            const newFiles = files.filter((_, j) => index !== j);

                            if (newFiles.every((f) => !f.isContractFile)) {
                              const contractFileCandidate = newFiles.find((f) =>
                                checkIsContractableFileName(f.name)
                              );

                              if (contractFileCandidate) {
                                contractFileCandidate.isContractFile = true;
                              }
                            }

                            return newFiles;
                          })
                        }
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </Flex>
                    </Flex>
                  );
                })}
              </Box>
            </Flex>
          ) : (
            <Flex
              alignItems="center"
              bg="boxBackgroundNeutralSlightlyLight"
              border="1px solid"
              borderColor="#e0e0e0"
              flexDirection="column"
              height="100%"
              justifyContent="center"
              width="100%"
            >
              <Text fontWeight="medium">계약서와 첨부파일을 업로드해 주세요.</Text>
              <Flex as="ul" flexDirection="column" gap="2px" mt={1} sx={{ listStyle: 'none' }}>
                {[
                  '지원 파일 : HWP, DOC, XLS, PPT, PDF, JPG, JPEG, PNG 등',
                  '업로드 가능 용량 : 각 파일당 최대 10MB',
                  '업로드 가능 개수 : 최대 21개',
                ].map((text) => (
                  <Text
                    as="li"
                    color="textNeutralLight"
                    fontSize="sm"
                    key={text}
                    textAlign="center"
                  >
                    <Flex alignItems="center" display="inline-flex" height="100%">
                      <CheckIcon
                        color="iconNeutralSlightlyDark"
                        height="12px"
                        mr={1}
                        variant="filled"
                        width="12px"
                      />
                    </Flex>
                    {text}
                  </Text>
                ))}
              </Flex>
              <Button
                fontSize="14px"
                isLoading={isValidating}
                mt={6}
                py={3}
                variant="primary"
                onClick={open}
              >
                <FontAwesomeIcon icon={faUpload} style={{ fontSize: '11px', marginRight: '3px' }} />
                업로드하기
              </Button>
            </Flex>
          ))}
      </Box>
    </Box>
  );
}
